/* eslint-disable react/require-default-props */
/* eslint-disable react/no-unstable-nested-components */
import { forwardRef } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Divider,
} from "@mui/material";
import {
  ExpandMore,
  Settings,
  Verified,
  InfoOutlined,
  CheckCircleOutline,
  LockPerson,
  FactCheck,
  PlaylistAddCheck,
} from "@mui/icons-material";
import ModalLayout from "src/components/shared/atoms/modalLayout";
import YouTubePreview from "src/components/shared/molecules/channelYoutubePreviewer";
import useStyle from "./Styles";

const FacebookInstructionsModal = forwardRef<
  any,
  { setModalState?: (open: boolean) => void }
>(({ setModalState }, ref) => {
  const { classes } = useStyle();

  const StepItem = ({
    icon,
    title,
    description,
    caution,
  }: {
    icon: any;
    title: string;
    description: string;
    caution?: string;
  }) => (
    <Box sx={{ display: "flex", mb: 3, gap: 2 }}>
      <Avatar
        sx={{
          background: "linear-gradient(45deg, #1877F2 0%, #0d47a1 100%)",
          color: "#fff",
          width: 40,
          height: 40,
          boxShadow: "0 2px 8px rgba(24, 119, 242, 0.25)",
        }}
      >
        {icon}
      </Avatar>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, mb: 0.5, color: "#2d3436" }}
        >
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: "#555", lineHeight: 1.6 }}>
          {description}
        </Typography>
        {caution && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 1,
              color: "#1877F2",
              fontStyle: "italic",
              fontWeight: 500,
            }}
          >
            {caution}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <ModalLayout
      headerText="Facebook Page Connection Flow"
      ref={ref}
      setModalState={setModalState}
      modalName={null}
      styleClass={classes.modalStyleClass}
      modalCustomStyle={{
        maxWidth: "600px",
        minHeight: "400px",
        width: "100%",
        borderRadius: "16px",
      }}
    >
      <Box
        sx={{
          maxHeight: "75vh",
          overflowY: "auto",
          p: 1,
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#e0e0e0",
            borderRadius: "10px",
          },
        }}
      >
        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            mb: 2,
            color: "#4d1277",
            fontFamily: "Poppins",
            textAlign: "center",
          }}
        >
          Connect Facebook Pages
        </Typography>

        {/* PREREQUISITE ALERT */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #faf5ff 0%, #fff 100%)",
            border: "1px solid #e9d5ff",
            borderRadius: "12px",
            p: 2.5,
            mb: 4,
            boxShadow: "0 4px 12px rgba(77, 18, 119, 0.05)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "4px",
              backgroundColor: "#1877F2",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
            <Verified sx={{ color: "#1877F2", fontSize: "1.5rem" }} />
            <Typography
              variant="subtitle2"
              sx={{ color: "#1877F2", fontWeight: 800, fontSize: "0.95rem" }}
            >
              REQUIRED FIRST STEP
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#2d3436", mb: 1 }}
          >
            Login with the Facebook account that OWNS or MANAGES your Pages.
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "#636e72", display: "block", mb: 2 }}
          >
            You must have Admin/Full Control permissions on the Pages you wish
            to connect.
          </Typography>
        </Box>

        <YouTubePreview videoId="ELf2MlpUUNY" />

        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 800, mb: 3, color: "#2d3436" }}
          >
            Connection Steps
          </Typography>

          <StepItem
            icon={<LockPerson fontSize="small" />}
            title="1. Sign in with Facebook"
            description="Authorize KuikWit to access your business profile. This won't post to your personal timeline."
          />

          <StepItem
            icon={<PlaylistAddCheck fontSize="small" />}
            title="2. Select Facebook Pages"
            description="Choose which specific Pages you want to integrate. You can select multiple pages at once."
            caution="Note: Only pages where you are an Admin will be visible."
          />

          <StepItem
            icon={<FactCheck fontSize="small" />}
            title="3. Grant Permissions"
            description="Meta will ask for access to messages, comments, and page stats. These are essential for the inbox to work."
            caution="Critical: Do not disable any requested permissions as it will break the integration."
          />

          <StepItem
            icon={<CheckCircleOutline fontSize="small" />}
            title="4. Finalize Connection"
            description="After the popup closes, click 'Connect' next to your page in the settings list to activate it."
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Accordions for secondary info */}
        <Box sx={{ mb: 2 }}>
          <Accordion
            elevation={0}
            sx={{
              "&:before": { display: "none" },
              border: "1px solid #f0f0f0",
              borderRadius: "8px !important",
              mb: 1.5,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: "#fafafa" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Settings fontSize="small" sx={{ color: "#1877F2" }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Permission Details
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ color: "#666", mb: 1.5 }}>
                • <strong>Messaging Access:</strong> Required to receive and
                send messages through KuikWit.
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", mb: 1.5 }}>
                • <strong>Comments & Posts:</strong> Required to sync comments
                on your posts with the dashboard.
              </Typography>
              <Typography variant="body2" sx={{ color: "#666" }}>
                • <strong>Page Management:</strong> Required for Meta to notify
                KuikWit of new events (webhooks).
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion
            elevation={0}
            sx={{
              "&:before": { display: "none" },
              border: "1px solid #f0f0f0",
              borderRadius: "8px !important",
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: "#fafafa" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <InfoOutlined fontSize="small" sx={{ color: "#1877F2" }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Troubleshooting
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: "#2d3436" }}
                >
                  "Wait, where are my pages?"
                </Typography>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Ensure you are the Page Admin and that you've selected the
                  correct business account during the sign-in flow.
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: "#2d3436" }}
                >
                  "Permission error"
                </Typography>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  If you accidentally skipped a permission, click 'Connect'
                  again and Meta will ask you to review and fix them.
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ mt: 2, fontSize: "0.8rem" }}>
                Need more help? Check the{" "}
                <a
                  href="https://kuikwit.com/facebook-integration"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#1877F2", fontWeight: 600 }}
                >
                  Full Integration Guide
                </a>
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Video Guide Link */}
        <Box mt={4} mb={2} textAlign="center">
          <Typography
            variant="body2"
            mt={2}
            sx={{ fontWeight: 600, color: "#1877F2" }}
          >
            <a
              href="https://kuikwit.com/facebook-integration"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "inherit",
                textDecoration: "underline",
              }}
            >
              View complete Facebook integration guide →
            </a>
          </Typography>
        </Box>
      </Box>
    </ModalLayout>
  );
});

export default FacebookInstructionsModal;
